import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiService } from '@/services/apiService'

export function useControlSignals(poolID: string) {
  return useQuery({
    queryKey: ['control', poolID],
    queryFn: async () => {
      const res = await apiService.control.getCurrent(poolID)
      if (!res.success) {
        const err: any = new Error(res.error ?? 'Failed to fetch control signals')
        err.errorCode = res.errorCode
        throw err
      }
      const data = res.data ?? []
      return Array.isArray(data) ? data : []
    },
    enabled: !!poolID,
    retry: false,
  })
}

export function useDeleteSignal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ poolID, taskID }: { poolID: string; taskID: string }) => {
      const res = await apiService.control.deleteSignal(poolID, taskID)
      if (!res.success) throw new Error(res.error ?? 'Failed to delete control task')
    },
    onSuccess: (_, { poolID }) => qc.invalidateQueries({ queryKey: ['control', poolID] }),
  })
}

export function useClearAllSignals() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ poolID }: { poolID: string }) => {
      const res = await apiService.control.deleteAll(poolID)
      if (!res.success) throw new Error(res.error ?? 'Failed to delete all control tasks')
    },
    onSuccess: (_, { poolID }) => qc.invalidateQueries({ queryKey: ['control', poolID] }),
  })
}

export function useSubmitControl() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      poolID,
      tasks,
      unit,
    }: {
      poolID: string
      tasks: Array<{ powerTarget: number; startTime: string; endTime: string }>
      unit?: 'W' | 'kW' | 'MW'
    }) => {
      const res = await apiService.control.submit(poolID, tasks, { unit })
      if (!res.success) throw new Error(res.error ?? 'Failed to submit control signals')
    },
    onSuccess: (_, { poolID }) => qc.invalidateQueries({ queryKey: ['control', poolID] }),
  })
}
